import React, { useEffect, useMemo, useState } from 'react';
import styles from './Software.module.scss';
import { useTranslation } from 'react-i18next';
import { WindowsIcon, IosIcon, DownloadIcon } from '../../../../components/Well/icons';
import { Accordion } from '../../../../components/Accordion/Accordion';
import cx from 'classnames';
import Spinner from '../../../../../common/components/Spinner';

export const Software = () => {
  const [showInstallers, setShowInstallers] = useState(false);

  const [windowsVersions, setWindowsVersions] = useState({});
  const [macVersions, setMacVersions] = useState({});

  const [isLoading, setIsLoading] = useState(true);

  const { t, i18n } = useTranslation();

  const macVersionsDescription = useMemo(() => {
    const { versions } = macVersions || {};
    return (
      versions?.map(({ version, description = {} }) => ({
        title: `${t('download_center.software.version')} ${version}`,
        content: description[i18n.language] || description?.en,
      })) || []
    );
  }, [macVersions, i18n.language]);

  const windowsVersionsDescription = useMemo(() => {
    const { versions } = windowsVersions || {};
    return (
      versions?.map(({ version, description = {} }) => ({
        title: `${t('download_center.software.version')} ${version}`,
        content: description[i18n.language] || description?.en,
      })) || []
    );
  }, [windowsVersions, i18n.language]);

  const installMacAppLink = useMemo(() => macVersions?.sources?.find(({ name }) => name === '.dmg'), [macVersions]);

  const installMicrosoftWindowsExeMicrosoftLink = useMemo(
    () => windowsVersions?.sources?.find(({ name, store }) => name === '.exe' && store),
    [windowsVersions]
  );
  const installMicrosoftWindowsExeAppLink = useMemo(
    () => windowsVersions?.sources?.find(({ name, store }) => name === '.exe' && !store),
    [windowsVersions]
  );

  const installMicrosoftWindowsMsiAppLink = useMemo(() => windowsVersions?.sources?.find(({ name }) => name === '.msi'), [
    windowsVersions,
  ]);
  const installMicrosoftWindowsMsiEnterpriseAppLink = useMemo(
    () => windowsVersions?.sources?.find(({ name }) => name === '.msi (Enterprise)'),
    [windowsVersions]
  );

  const toggleInstallers = () => {
    setShowInstallers(!showInstallers);
  };

  useEffect(() => {
    const getVersionsDescriptionInfo = async () => {
      try {
        setIsLoading(true);
        const resp = await fetch(
          'https://prod-static-video-resources.s3.eu-west-1.amazonaws.com/manual-uploads/desktop_versions.json'
        );
        const data = await resp.json();

        const windows = data?.find(({ os }) => os === 'windows') || {};
        const mac = data?.find(({ os }) => os === 'mac') || {};

        setWindowsVersions(windows);
        setMacVersions(mac);
      } catch (err) {
        console.log(err);
      }

      setIsLoading(false);
    };

    getVersionsDescriptionInfo();
  }, []);

  return (
    <div className={styles.softwareContainer}>
      <div className={`${styles.softwareTitle} ${styles.title}`}>{t('download_center.software')}</div>
      {isLoading ? (
        <Spinner width="40px" height="40px" size="4px" className={styles.spinner} />
      ) : (
        <>
          <div className={styles.softwareItem}>
            <div className={styles.itemTitleContainer}>
              <div className={styles.itemTitle}>
                <IosIcon />
                <div className={styles.title}>{t('download_center.software.download_mac_desktop_app')}</div>
              </div>
              <div className={styles.buttonContainer}>
                <a href={installMacAppLink?.link} className={`${styles.download} ${styles.aButton}`} target="_blank">
                  <DownloadIcon />
                  {t('general.download')}
                </a>
              </div>
            </div>
              <Accordion data={macVersionsDescription}></Accordion>
          </div>
          <div className={styles.softwareItem}>
            <div className={styles.itemTitleContainer}>
              <div className={styles.itemTitle}>
                <WindowsIcon />
                <div className={styles.title}>{t('download_center.software.download_microsoft_desktop_app')}</div>
              </div>
              <div className={styles.actionContainer}>
                <div className={styles.buttonContainer}>
                  <a
                    href={installMicrosoftWindowsExeMicrosoftLink?.link}
                    className={`${styles.download} ${styles.aButton}`}
                    target="_blank"
                  >
                    <DownloadIcon />
                    {t('general.download')}
                  </a>
                  <div className={styles.installersText} onClick={toggleInstallers}>
                    <a>Installers</a>
                  </div>
                </div>
              </div>
            </div>

            <div className={cx(styles.installersContainer, { [styles.installersContainerActive]: showInstallers })}>
              <a href={installMicrosoftWindowsExeAppLink?.link} className={styles.seperateLink} target="_blank">
                {installMicrosoftWindowsExeAppLink?.name}
              </a>{' '}
              <a href={installMicrosoftWindowsMsiAppLink?.link} className={styles.seperateLink} target="_blank">
                {installMicrosoftWindowsMsiAppLink?.name}
              </a>{' '}
              <a href={installMicrosoftWindowsMsiEnterpriseAppLink?.link} className={styles.seperateLink} target="_blank">
                {installMicrosoftWindowsMsiEnterpriseAppLink?.name}
              </a>
            </div>

              <Accordion data={windowsVersionsDescription}></Accordion>
          </div>
        </>
      )}
    </div>
  );
};
